import { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, LogOut, Settings as SettingsIcon, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { relativeTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import NueraLogo from "@/components/NueraLogo";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/website", label: "Website", testid: "nav-website" },
  { to: "/social", label: "Social", testid: "nav-social" },
  { to: "/paid-ads", label: "Paid Ads", testid: "nav-paid-ads" },
  { to: "/leads", label: "Leads", testid: "nav-leads" },
];

function NavPill({ className = "" }) {
  return (
    <nav className={`flex items-center gap-1 rounded-full border border-border bg-card p-1 shadow-[0_2px_12px_rgba(0,0,0,0.05)] ${className}`}>
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          data-testid={n.testid}
          className={({ isActive }) =>
            `whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              isActive ? "bg-brand-primary text-brand-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`
          }
        >
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [syncing, setSyncing] = useState(false);

  const { data: syncStatus } = useQuery({
    queryKey: ["sync-status"],
    queryFn: async () => (await api.get("/sync/status")).data,
    refetchInterval: 120000,
  });

  const canSync = user?.role === "admin" || user?.role === "manager";

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sync/now");
      toast.success(`Sync complete — ${data.records.toLocaleString()} records refreshed`);
      qc.invalidateQueries();
    } catch {
      toast.error("Sync failed. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="relative mx-auto flex max-w-[1400px] items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Link to="/" className="shrink-0"><NueraLogo className="h-7 w-auto" /></Link>

          <div className="absolute left-1/2 hidden -translate-x-1/2 lg:block">
            <NavPill />
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden items-center gap-1.5 sm:flex" title="Platform sync status">
              {syncStatus?.platforms?.map((p) => (
                <span key={p.platform} className={`h-2 w-2 rounded-full ${p.status === "connected" ? "bg-emerald-500" : "bg-amber-500"}`} />
              ))}
            </div>
            <span className="hidden text-xs text-muted-foreground md:inline">Updated {relativeTime(syncStatus?.last_updated)}</span>
            {canSync && (
              <Button data-testid="sync-now-btn" onClick={doSync} disabled={syncing} size="sm" className="nuera-gradient rounded-full text-white hover:opacity-90">
                <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline">{syncing ? "Syncing" : "Sync Now"}</span>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-1.5 rounded-full border border-border bg-card p-1 pr-2 transition-shadow hover:shadow-sm">
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-brand-primary/15 text-xs font-bold text-foreground">{user?.name?.[0]?.toUpperCase() || "U"}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-xs font-normal capitalize text-muted-foreground">{user?.role} · {user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}>
                  <SettingsIcon className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="nuera-scroll overflow-x-auto px-4 pb-3 lg:hidden">
          <NavPill className="w-max" />
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</main>
    </div>
  );
}
